function TableView({ jobs, onOpen, upsert, sortOption, setSortOption, bulkMode, selectedIds, onToggleSelect, onAdd, onImportCSV, onDownloadTemplate, renderProBadge }) {
  const { useState, useMemo } = React;
  const [inline, setInline] = useState({
    company: '', title: '', date: todayISO(),
    status: 'waiting', salary: '', customSalary: '', notes: '', contactName: '', link: '', favorite: false,
  });

  // Mobile quick-edit state
  const [expandedId, setExpandedId] = useState(null);
  const [editDraft, setEditDraft] = useState(null);

  const sort = sortFromOption(sortOption);

  const sorted = useMemo(() => {
    const arr = [...jobs];
    arr.sort((a, b) => {
      if (sort.key === 'date') {
        return compareJobsByAppliedDate(a, b, sort.dir);
      }

      if (sort.key === 'salary') {
        const av = salarySortNumber(a.salary);
        const bv = salarySortNumber(b.salary);

        if (av === -Infinity && bv === -Infinity) return 0;
        if (av === -Infinity) return 1;
        if (bv === -Infinity) return -1;
        return sort.dir === 'asc' ? av - bv : bv - av;
      }

      if (sort.key === 'status') {
        const av = statusSortNumber(a.status);
        const bv = statusSortNumber(b.status);
        return sort.dir === 'asc' ? av - bv : bv - av;
      }

      const av = (a[sort.key] || '').toString().toLowerCase();
      const bv = (b[sort.key] || '').toString().toLowerCase();
      if (av < bv) return sort.dir === 'asc' ? -1 : 1;
      if (av > bv) return sort.dir === 'asc' ?  1 : -1;
      return 0;
    });
    return arr;
  }, [jobs, sort.key, sort.dir]);

  const titleSuggestions = useMemo(() => (
    [...new Set(
      jobs
        .map(job => job.title)
        .filter(Boolean)
        .map(title => title.trim())
        .filter(Boolean)
    )].sort((a, b) => a.localeCompare(b))
  ), [jobs]);

  const head = (k, label) => {
    const ascOption = sortOptionFromParts(k, 'asc');
    const descOption = sortOptionFromParts(k, 'desc');
    if (!ascOption && !descOption) return <th className="no-sort">{label}</th>;

    return (
      <th onClick={() => {
        const nextDir = ascOption && descOption
          ? (sort.key === k && sort.dir === 'desc' ? 'asc' : 'desc')
          : (ascOption ? 'asc' : 'desc');
        const nextOption = sortOptionFromParts(k, nextDir);
        if (nextOption && setSortOption) setSortOption(nextOption);
      }}>
        {label} {sort.key === k ? (sort.dir === 'asc' ? '↑' : '↓') : ''}
      </th>
    );
  };

  const displaySalary = (value) => {
    if (!value) return '—';
    return formatSalaryCompact(value) || formatSalaryValue(value);
  };

  const submitInline = async () => {
    if (!inline.company.trim() || !inline.title.trim()) return;
    const saved = await upsert({
      ...inline,
      id: uid(),
      salary: inline.salary === 'custom' ? inline.customSalary.trim() : inline.salary,
      favorite: inline.favorite,
    });
    if (saved === false) return;
    setInline({
      company: '', title: '', date: todayISO(),
      status: 'waiting', salary: '', customSalary: '', notes: '', contactName: '', link: '', favorite: false,
    });
  };

  // Mobile edit handlers
  const today = todayISO();

  const openMobEdit = (j) => {
    setExpandedId(j.id);
    setEditDraft({ ...j });
  };

  const closeMobEdit = () => {
    setExpandedId(null);
    setEditDraft(null);
  };

  const saveMobEdit = async () => {
    if (!editDraft) return;
    const saved = await upsert(editDraft);
    if (saved !== false) closeMobEdit();
  };

  return (
    <>
      {/* ── Desktop table (hidden on mobile via CSS) ── */}
      <div className="sketch-box table-wrap">
        <table>
          <thead>
            <tr>
              {bulkMode && <th style={{ width: 32, padding: '0 8px' }}></th>}
              {head('company', 'Company')}
              {head('title',   'Job title')}
              {head('date',    'Applied')}
              {head('status',  'Status')}
              {head('salary',  'Salary')}
              <th>Contact</th>
              <th>Notes</th>
              <th>Listing</th>
              <th>Fav</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {/* Inline add row — always visible at top of table */}
            <tr className="inline-add">
              {bulkMode && <td></td>}
              <td><input placeholder="Company *" value={inline.company} onChange={e => setInline(s => ({ ...s, company: e.target.value }))} onKeyDown={e => e.key === 'Enter' && submitInline()} /></td>
              <td>
                <input
                  placeholder="Job title *"
                  list="table-job-title-suggestions"
                  value={inline.title}
                  onChange={e => setInline(s => ({ ...s, title: e.target.value }))}
                  onKeyDown={e => e.key === 'Enter' && submitInline()}
                />
                <datalist id="table-job-title-suggestions">
                  {titleSuggestions.map(title => (
                    <option key={title} value={title} />
                  ))}
                </datalist>
              </td>
              <td><input type="date" value={inline.date} onChange={e => setInline(s => ({ ...s, date: e.target.value }))} /></td>
              <td>
                <select value={inline.status} onChange={e => setInline(s => ({ ...s, status: e.target.value }))}>
                  {STATUSES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                </select>
              </td>
              <td>
                <select value={inline.salary} onChange={e => setInline(s => ({ ...s, salary: e.target.value }))}>
                  <option value="">$–$</option>
                  <option value="<$100">&lt;$100</option>
                  <option value="$100 - $130">$100 - $130</option>
                  <option value="$130 - $150">$130 - $150</option>
                  <option value="$150 - $200">$150 - $200</option>
                  <option value="$200+">$200+</option>
                  <option value="custom">Custom</option>
                </select>
                {inline.salary === 'custom' && (
                  <input
                    placeholder="$120 - $150"
                    value={inline.customSalary}
                    onChange={e => setInline(s => ({ ...s, customSalary: e.target.value }))}
                    onKeyDown={e => e.key === 'Enter' && submitInline()}
                    style={{ marginTop: 6 }}
                  />
                )}
              </td>
              <td><input placeholder="Point of contact name" value={inline.contactName} onChange={e => setInline(s => ({ ...s, contactName: e.target.value }))} /></td>
              <td><input placeholder="notes…" value={inline.notes} onChange={e => setInline(s => ({ ...s, notes: e.target.value }))} /></td>
              <td><input placeholder="listing link" value={inline.link} onChange={e => setInline(s => ({ ...s, link: e.target.value }))} onKeyDown={e => e.key === 'Enter' && submitInline()} /></td>
              <td style={{ textAlign: 'center' }}>
                <input
                  type="checkbox"
                  checked={inline.favorite}
                  onChange={e => setInline(s => ({ ...s, favorite: e.target.checked }))}
                  title="Add to favorites"
                />
              </td>
              <td>
                <button className="sketch-btn primary" onClick={submitInline} style={{ padding: '4px 10px', fontSize: 13 }}>
                  add
                </button>
              </td>
            </tr>

            {sorted.length === 0 && jobs.length === 0 && (
              <tr>
                <td colSpan={bulkMode ? 11 : 10} style={{ padding: '48px 24px', textAlign: 'center', borderBottom: 'none' }}>
                  <div className="view-empty-title">No jobs yet</div>
                  <div className="view-empty-desc">
                    This table will populate as you add jobs. Start by adding one above <span className="material-icons" aria-hidden="true" style={{ fontSize: 14, verticalAlign: 'middle' }}>north</span> or uploading a CSV.
                  </div>
                  <div className="view-empty-actions">
                    {onImportCSV && <button className="sketch-btn" onClick={onImportCSV}>Import CSV {renderProBadge && renderProBadge()}</button>}
                  </div>
                  {onDownloadTemplate && (
                    <button className="view-empty-link" onClick={onDownloadTemplate}>Download CSV Template</button>
                  )}
                </td>
              </tr>
            )}
            {sorted.length === 0 && jobs.length > 0 && (
              <tr><td colSpan={bulkMode ? 11 : 10} className="empty">no rows match your filters</td></tr>
            )}

            {sorted.map(j => (
              <tr
                key={j.id}
                className={bulkMode && selectedIds.has(j.id) ? 'bulk-selected' : ''}
                onClick={() => bulkMode ? onToggleSelect(j.id) : onOpen(j)}
                style={{ cursor: 'pointer' }}
              >
                {bulkMode && (
                  <td style={{ padding: '0 8px', textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      className="bulk-cb"
                      checked={selectedIds.has(j.id)}
                      onChange={() => onToggleSelect(j.id)}
                      onClick={e => e.stopPropagation()}
                    />
                  </td>
                )}
                <td>
                  <b>{j.favorite && <span className="material-icons favorite-heart">auto_awesome</span>}{j.company}</b>
                </td>
                <td>{j.title}</td>
                <td>{prettyDate(j.date)}</td>
                <td>
                  <span className={`pill ${STATUS_MAP[j.status]?.cls || ''}`}>
                    {STATUS_MAP[j.status]?.label || j.status}
                  </span>
                </td>
                <td>{displaySalary(j.salary)}</td>
                <td style={{ fontSize: 13, maxWidth: 180, minWidth: 0, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{j.contactName || j.contact || j.contactEmail || '—'}</td>
                <td style={{ fontSize: 13, maxWidth: 180, minWidth: 0, color: 'var(--ink-soft)', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{j.notes || '—'}</td>
                <td>
                  {safeHref(j.link) ? (
                    <a
                      className="candy-btn link-purple table-listing-btn"
                      href={safeHref(j.link)}
                      target="_blank"
                      rel="noreferrer"
                      onClick={e => e.stopPropagation()}
                    >
                      View listing
                    </a>
                  ) : '—'}
                </td>
                <td style={{ textAlign: 'center' }}>
                  {j.favorite ? <span className="material-icons favorite-heart">auto_awesome</span> : '—'}
                </td>
                <td style={{ textAlign: 'right', color: 'var(--ink-faint)' }}>›</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Mobile data-table view (hidden on desktop via CSS) ── */}
      <div className="mob-table-list">
        {sorted.length === 0 && jobs.length === 0 && (
          <div className="mob-table-empty">
            <div className="mob-table-empty-title">No jobs yet</div>
            <div className="mob-table-empty-desc">This table will populate as you add jobs. Start by adding one above or uploading a CSV.</div>
            {onImportCSV && (
              <button className="sketch-btn" style={{ marginTop: 12 }} onClick={onImportCSV}>
                Import CSV {renderProBadge && renderProBadge()}
              </button>
            )}
            {onDownloadTemplate && (
              <button className="view-empty-link" style={{ marginTop: 8 }} onClick={onDownloadTemplate}>
                Download CSV Template
              </button>
            )}
          </div>
        )}
        {sorted.length === 0 && jobs.length > 0 && (
          <div className="mob-table-empty">
            <div className="mob-table-empty-desc">No jobs match your filters.</div>
          </div>
        )}

        {sorted.length > 0 && (
          <div className="mob-tbl">
            {/* ── Header row ── */}
            <div className={`mob-tbl-head${bulkMode ? ' has-check' : ''}`}>
              {bulkMode && <div className="mob-tbl-hcell mob-tbl-hcell-check"></div>}
              <div className="mob-tbl-hcell">Company / Role</div>
              <div className="mob-tbl-hcell mob-tbl-hcell-status">Status</div>
              <div className="mob-tbl-hcell mob-tbl-hcell-exp"></div>
            </div>

            {/* ── Data rows ── */}
            {sorted.map(j => {
              const isExpanded = expandedId === j.id;
              const isSelected = bulkMode && selectedIds.has(j.id);
              const old = daysBetween(j.date, today);
              const stale = j.status === 'applied' && old != null && old >= STALE_DAYS;

              return (
                <div key={j.id} className={`mob-tr${isExpanded ? ' expanded' : ''}${isSelected ? ' bulk-selected' : ''}`}>

                  {/* Collapsed table row — grid aligns with header */}
                  <div
                    className={`mob-tr-head${bulkMode ? ' has-check' : ''}`}
                    onClick={() => {
                      if (bulkMode) { onToggleSelect(j.id); return; }
                      if (isExpanded) { closeMobEdit(); } else { openMobEdit(j); }
                    }}
                  >
                    {bulkMode && (
                      <input
                        type="checkbox"
                        className="bulk-cb mob-tr-cb"
                        checked={isSelected}
                        onChange={() => onToggleSelect(j.id)}
                        onClick={e => e.stopPropagation()}
                      />
                    )}
                    <div className="mob-tr-job">
                      <div className="mob-tr-co">
                        {stale && <span className="stale-dot" title={`${old}d old`}></span>}
                        {j.favorite && <span className="material-icons favorite-heart" style={{ fontSize: 12 }}>auto_awesome</span>}
                        {j.company}
                      </div>
                      <div className="mob-tr-title">{j.title}</div>
                      {prettyDate(j.date) && (
                        <div className="mob-tr-date">{prettyDate(j.date)}</div>
                      )}
                    </div>
                    <div className="mob-tr-status">
                      <span className={`pill ${STATUS_MAP[j.status]?.cls || ''}`}>
                        {STATUS_MAP[j.status]?.label || j.status}
                      </span>
                    </div>
                    <div className="mob-tr-exp">
                      <span className="material-icons mob-tr-chevron">
                        {isExpanded ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
                      </span>
                    </div>
                  </div>

                  {/* Inline edit panel — unchanged */}
                  {isExpanded && editDraft && (
                    <div className="mob-tr-edit">
                      <div className="mob-tr-row2">
                        <div className="mob-tr-field">
                          <label>Company</label>
                          <input
                            className="sketch-input"
                            value={editDraft.company || ''}
                            onChange={e => setEditDraft(d => ({ ...d, company: e.target.value }))}
                            placeholder="Company"
                          />
                        </div>
                        <div className="mob-tr-field">
                          <label>Job title</label>
                          <input
                            className="sketch-input"
                            value={editDraft.title || ''}
                            onChange={e => setEditDraft(d => ({ ...d, title: e.target.value }))}
                            placeholder="Job title"
                          />
                        </div>
                      </div>

                      <div className="mob-tr-row2">
                        <div className="mob-tr-field">
                          <label>Status</label>
                          <select
                            className="sketch-input"
                            value={editDraft.status || 'waiting'}
                            onChange={e => setEditDraft(d => ({ ...d, status: e.target.value }))}
                          >
                            {STATUSES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                          </select>
                        </div>
                        <div className="mob-tr-field">
                          <label>Applied</label>
                          <input
                            className="sketch-input"
                            type="date"
                            value={editDraft.date || ''}
                            onChange={e => setEditDraft(d => ({ ...d, date: e.target.value }))}
                          />
                        </div>
                      </div>

                      <div className="mob-tr-row2">
                        <div className="mob-tr-field">
                          <label>Salary</label>
                          <input
                            className="sketch-input"
                            value={editDraft.salary || ''}
                            onChange={e => setEditDraft(d => ({ ...d, salary: e.target.value }))}
                            placeholder="e.g. $120k – $150k"
                          />
                        </div>
                        <div className="mob-tr-field">
                          <label>Contact</label>
                          <input
                            className="sketch-input"
                            value={editDraft.contactName ?? editDraft.contact ?? ''}
                            onChange={e => setEditDraft(d => ({ ...d, contactName: e.target.value }))}
                            placeholder="Recruiter name"
                          />
                        </div>
                      </div>

                      <div className="mob-tr-field">
                        <label>Link</label>
                        <input
                          className="sketch-input"
                          value={editDraft.link || ''}
                          onChange={e => setEditDraft(d => ({ ...d, link: e.target.value }))}
                          placeholder="https://…"
                        />
                      </div>

                      <div className="mob-tr-field">
                        <label>Notes</label>
                        <textarea
                          className="sketch-input mob-tr-notes"
                          value={editDraft.notes || ''}
                          onChange={e => setEditDraft(d => ({ ...d, notes: e.target.value }))}
                          placeholder="Notes…"
                          rows={3}
                        />
                      </div>

                      <label className="mob-tr-fav-toggle">
                        <input
                          type="checkbox"
                          checked={!!editDraft.favorite}
                          onChange={e => setEditDraft(d => ({ ...d, favorite: e.target.checked }))}
                        />
                        <span className="material-icons favorite-heart" style={{ fontSize: 16 }}>auto_awesome</span>
                        <span>{editDraft.favorite ? 'Favorited' : 'Add to favorites'}</span>
                      </label>

                      <div className="mob-tr-edit-actions">
                        <button
                          type="button"
                          className="sketch-btn mob-tr-full-edit-btn"
                          onClick={() => { onOpen(editDraft); closeMobEdit(); }}
                        >
                          Full detail ›
                        </button>
                        <div className="mob-tr-edit-actions-right">
                          <button type="button" className="sketch-btn" onClick={closeMobEdit}>Cancel</button>
                          <button type="button" className="sketch-btn primary" onClick={saveMobEdit}>Save</button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
